import { Actions, Async, FSpread } from '@client/new/helpers/types';
import {
  GetHomePageDataQuery,
  ProgramAccessType,
  useCreateDraftCollectionMutation,
  useGetHomePageDataQuery,
  useSetCreatorAsLiveMutation,
} from 'generated/graphql';
import { useUserStore } from '@client/new/stores/user.store';
import { browserHistory } from 'react-router';
import { PATHS } from '@client/routes';
import toast from 'react-hot-toast';
import { useState } from 'react';
import { BusinessModel } from '@playbook-technologies/common-enums';

export type HomeModel = {
  dispatch: HomeModelDispatcher;
  goLive: () => Promise<void>;
  submittingOffer: boolean;
  goLiveProgress: boolean;
  uid: string;
  data: GetHomePageDataQuery;
  creatorWentLive: boolean;
  influencerId: number;
  isGuideVisible: boolean;
  isOfferMigratedCreator: boolean;
};

export type PayloadMap = {
  createOffer: Offer;
};
export type HomeModelActions = Actions<PayloadMap>;
export type HomeModelDispatcher = FSpread<PayloadMap>;

export type CollectionType = 'otp' | 'subscription';

export type Offer = {
  name: string;
  type: CollectionType;
};

export const useHomeModel = (p: {
  influencerId?: string;
}): Async<HomeModel> => {
  const [creatorWentLive, setCreatorWentLive] = useState(false);
  const { user } = useUserStore();
  const influencerId = p?.influencerId
    ? Number(p?.influencerId)
    : user.influencerId;

  const { data, loading, error, refetch } = useGetHomePageDataQuery({
    variables: {
      creatorId: influencerId,
      creatorIdStr: String(influencerId),
    },
    skip: !influencerId,
    fetchPolicy: 'network-only',
  });

  const [setCreatorAsLiveMutation, setCreatorAsLiveMutationState] =
    useSetCreatorAsLiveMutation({
      variables: {
        creatorId: influencerId,
      },
    });

  const [createDraftCollectionMutation, useCreateDraftCollectionMutationState] =
    useCreateDraftCollectionMutation();

  const uid = `${user.id}`;

  const dispatch: HomeModelDispatcher = (type, ...args) => {
    const action = { type, payload: args[0] } as HomeModelActions;
    switch (action.type) {
      case 'createOffer':
        createOffer(action.payload);
        return null;
    }
  };

  if (loading) return { type: 'fetching' };

  if (error || !data?.creator)
    return { type: 'fetch-error', value: 'Could not fetch data' };

  const influencer = data?.creator;

  const createOffer = async (offer: Offer) => {
    const accessType =
      offer.type === 'otp'
        ? ProgramAccessType.Exclusive
        : ProgramAccessType.Premium;

    createDraftCollectionMutation({
      variables: {
        data: {
          accessType,
          creatorId: influencer.id,
          name: offer.name,
          type: 0,
        },
      },
    })
      .then(() => {
        const path =
          offer.type === 'otp'
            ? PATHS.PURCHASES(influencer.id)
            : PATHS.SUBSCRIPTIONS(influencer.id);
        browserHistory.push(path);
      })
      .catch((error) => {
        toast.error(error.message);
      });
  };

  const goLive = async (): Promise<void> => {
    await setCreatorAsLiveMutation();
    setCreatorWentLive(true);
    await refetch();
  };

  if (loading) return { type: 'fetching' };

  if (error || !data?.creator)
    return { type: 'fetch-error', value: 'Could not fetch data' };

  const hasActiveStrategy =
    data.GetStrategies?.some((s) => s.isSelected) ?? false;
  const isGuideVisible = hasActiveStrategy;
  const isOfferMigratedCreator =
    data.creator.businessModel === BusinessModel.Bundle &&
    !!data.creator.customizations?.isExclusiveMigratedToBundle;

  return {
    type: 'fetched',
    value: {
      dispatch,
      goLive,
      submittingOffer: useCreateDraftCollectionMutationState.loading,
      goLiveProgress: setCreatorAsLiveMutationState.loading,
      uid,
      data,
      creatorWentLive,
      influencerId,
      isGuideVisible,
      isOfferMigratedCreator,
    },
  };
};
